import { Component, Inject, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { TemplateSelector } from '../../shared/components/template-selector/template-selector.component';
import { Template } from '../../_interfaces/template';
import { DefaultTemplate } from '../../_interfaces/user-settings.interface';
import { AuthService } from '../../_services/auth.service';
import { APP_DIALOG_DATA } from '../../shared/components/dialog/dialog.tokens';
import { AppDialogRef } from '../../shared/components/dialog/dialog-ref';
import { DIALOG_IMPORTS } from '../../shared/components/dialog/dialog.imports';

export interface TemplatesStepDialogData {
  templates: Template[];
  selectedTemplates: DefaultTemplate[];
}

@Component({
  selector: 'app-templates-step-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ...DIALOG_IMPORTS,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
    TemplateSelector,
  ],
  template: `
    <div class="flex flex-col h-[90dvh] max-h-full min-h-0 overflow-hidden">
      <div class="p-6 flex flex-col gap-4 shrink-0">
        <div class="flex items-center justify-between mb-2">
          <h1 class="ml-10 headline-large text-primary flex-1 text-center">
            {{ 'Select blueprints' | translate }}
          </h1>

          <button
            mat-icon-button
            (click)="onCancel()"
            [attr.aria-label]="'Close' | translate"
          >
            <mat-icon class="text-on-surface-variant">close</mat-icon>
          </button>
        </div>

        @if (showBlueprints) {
          <p class="body-large text-inverse-surface">
            {{
              'Choose between the blueprints set up for your organisation, or our full library of T-Pro curated blueprints'
                | translate
            }}
          </p>
        }
      </div>

      <!-- Fills remaining height -->
      <div class="px-6 flex-1 min-h-0 flex flex-col mb-6 overflow-hidden">
        <app-template-selector
          class="flex-1 min-h-0 flex flex-col"
          [templates]="data.templates"
          [selectedTemplates]="data.selectedTemplates"
          (selectionChange)="onSelectionChange($event)"
        ></app-template-selector>
      </div>

      <app-dialog-footer class="shrink-0">
        <div class="flex w-full gap-2">
          <button
            class="w-full"
            mat-stroked-button
            (click)="onCancel()"
          >
            {{ 'Cancel' | translate }}
          </button>

          <button
            class="w-full"
            mat-flat-button
            (click)="onConfirm()"
          >
            {{ 'Confirm selection' | translate }}
          </button>
        </div>
      </app-dialog-footer>

    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
    :host ::ng-deep app-template-selector { width: 100%; }
  `],
})
export class TemplatesStepDialogComponent {
  currentSelection: Template[] = [];
  private readonly authService = inject(AuthService);

  constructor(
    @Inject(APP_DIALOG_DATA) public data: TemplatesStepDialogData,
    private dialogRef: AppDialogRef<TemplatesStepDialogComponent>,
  ) {}

  get showBlueprints(): boolean {
    return this.authService.isAiBlueprintsEnabled;
  }

  onSelectionChange(templates: Template[]): void {
    this.currentSelection = templates;
  }

  onConfirm(): void {
    this.dialogRef.close(this.currentSelection);
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
